import { Button, Card, Checkbox, Form, Input, Radio, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    Grid as ZhGrid,
    InputNumber as ZhInputNumber,
    Upload as ZhUpload,
    type UploadRequestOptions,
    type UploadUserFile
} from "zhihao-ui-react";

import request from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { DEFAULT_VALUE } from "@/config";
import { POP_UP_AD_FUNCTION_PAGE } from "@zhihaoscm/meta";
import type { PopUpAdvertDetail } from "./PopUpAdvertDetailPage";
import { loadPopUpAdvertDetail } from "./PopUpAdvertDetailPage";

interface PopUpAdvertContentForm {
    picFileId?: number;
    picFileUrl?: string;
    type?: number;
    value?: string;
    sort?: number;
}

export interface PopUpAdvertFormValues {
    name: string;
    terminal: number[];
    displayPage?: number;
    displayFrequency?: number;
    content: PopUpAdvertContentForm[];
}

export interface PopUpAdvertFormPageProps {
    id?: number;
    loadDetail?: (id: number) => Promise<PopUpAdvertDetail>;
    loadPicture?: (id: number) => Promise<{ path?: string }>;
    loadContentOptions?: (
        type: number | string,
        keyword: string,
        terminalType: number
    ) => Promise<PopUpAdvertSelectorOption[]>;
    create?: (values: PopUpAdvertFormValues) => Promise<unknown>;
    update?: (id: number, values: PopUpAdvertFormValues) => Promise<unknown>;
}

export interface PopUpAdvertSelectorOption {
    id: number | string;
    label: string;
}

const displayPages = [
    { value: 1, label: "首页" },
    { value: 2, label: "砂石指数首页" },
    { value: 3, label: "运价指数首页" },
    { value: 4, label: "资讯首页" },
    { value: 5, label: "查船页" },
    { value: 6, label: "查车页" },
    { value: 7, label: "船运需求列表" },
    { value: 8, label: "运单页" },
    { value: 11, label: "会员页" },
    { value: 12, label: "认证页" },
    { value: 13, label: "关于我们" }
];
const frequencies = [
    { value: 1, label: "每天一次" },
    { value: 2, label: "每周一次" },
    { value: 3, label: "仅一次" },
    { value: 4, label: "每次打开页面" }
];
const contentTypes = [
    { value: 1, label: "商品" },
    { value: 2, label: "砂价" },
    { value: 3, label: "运价" },
    { value: 4, label: "广告页" },
    { value: 5, label: "资讯" },
    { value: 6, label: "功能页" },
    { value: 7, label: "链接" }
];
const remoteContentTypeLabels: Record<number, { label: string; placeholder: string }> = {
    1: { label: "商品", placeholder: "请搜索商品名称或商品id选择" },
    2: { label: "砂价", placeholder: "请搜索品类名称或品类代码选择" },
    3: { label: "运价", placeholder: "请搜索码头名称或航线代码选择" },
    4: { label: "广告页", placeholder: "请搜索广告页标题或编号选择" },
    5: { label: "资讯", placeholder: "请搜索资讯标题" },
    6: { label: "功能页", placeholder: "请搜索功能页" }
};
const defaultContent: PopUpAdvertContentForm = { picFileId: undefined, type: undefined, value: "", sort: 1 };
const defaultValues: PopUpAdvertFormValues = { name: "", terminal: [1], content: [{ ...defaultContent }] };
const POP_UP_IMAGE_TYPES = new Set(["image/jpeg", "image/png"]);
const POP_UP_IMAGE_MAX_SIZE = 5 * 1024 * 1024;

/** 对齐旧 Vue ad-img 的图片类型与大小限制。 */
function isValidPopUpImage(file: File): boolean {
    return POP_UP_IMAGE_TYPES.has(file.type) && file.size < POP_UP_IMAGE_MAX_SIZE;
}

/** 复用 Vue 弹窗广告新增接口。 */
export async function createPopUpAdvert(values: PopUpAdvertFormValues): Promise<unknown> {
    const response = await request.service.post("/dataService/admin/pop-up-advert", values);
    return response.data;
}

/** 复用 Vue 弹窗广告编辑接口。 */
export async function updatePopUpAdvert(id: number, values: PopUpAdvertFormValues): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/pop-up-advert/${id}`, values);
    return response.data;
}

/** 编辑弹窗时沿用旧 Vue ad-img 组件的已上传图片回显。 */
export async function loadPopUpAdvertFormPicture(id: number): Promise<{ path?: string }> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${id}`);
    return response.data;
}

interface PopUpAdvertProductSelectorItem {
    id: number | string;
    title?: string;
    productTypeName?: string;
    productTypeSpec?: string | number;
}

interface PopUpAdvertProductTypeSelectorItem {
    id: number | string;
    name?: string;
    area?: string;
}

interface PopUpAdvertRouteSelectorItem {
    id: number | string;
    source?: string;
    destination?: string;
    sourceFullName?: string;
    destinationFullName?: string;
}

interface PopUpAdvertAdPageSelectorItem {
    advert?: { id: number | string; title?: string };
}

interface PopUpAdvertInformationSelectorItem {
    id: number | string;
    title?: string;
}

/** 复用 Vue 弹窗广告编辑页的远程跳转值查询和选项文案。 */
export async function loadPopUpAdvertContentOptions(
    type: number | string,
    keyword: string,
    terminalType: number
): Promise<PopUpAdvertSelectorOption[]> {
    if (!keyword.trim()) return [];
    switch (Number(type)) {
        case 1: {
            const response = await request.service.get<{ list?: PopUpAdvertProductSelectorItem[] }>(
                "/admin/product-consignment/paging-selector",
                { params: { keyword, page: 1, size: 50 } }
            );
            return (response.data.list || []).map(item => ({
                id: item.id,
                label: [
                    item.title,
                    item.productTypeName && `${item.productTypeName}(${item.productTypeSpec ?? ""})`,
                    item.id
                ]
                    .filter(Boolean)
                    .join(" | ")
            }));
        }
        case 2: {
            const response = await request.service.get<{ list?: PopUpAdvertProductTypeSelectorItem[] }>(
                "/dataService/admin/product-type/paging-selector",
                { params: { keyword, page: 1, size: 50 } }
            );
            return (response.data.list || []).map(item => ({
                id: item.id,
                label: [item.name, item.area].filter(Boolean).join(" | ")
            }));
        }
        case 3: {
            const response = await request.service.get<{ list?: PopUpAdvertRouteSelectorItem[] }>(
                "/dataService/admin/ship/route/paging-selector",
                { params: { shipRouteName: keyword, page: 1, size: 50 } }
            );
            return (response.data.list || []).map(item => ({
                id: item.id,
                label: [
                    item.source && item.destination ? `${item.source}-${item.destination}` : undefined,
                    item.sourceFullName && item.destinationFullName
                        ? `${item.sourceFullName}-${item.destinationFullName}`
                        : undefined
                ]
                    .filter(Boolean)
                    .join(" | ")
            }));
        }
        case 4: {
            const response = await request.service.get<{ list?: PopUpAdvertAdPageSelectorItem[] }>(
                "/dataService/admin/advert/paging",
                { params: { key: keyword, classify: terminalType, page: 1, size: 50 } }
            );
            return (response.data.list || [])
                .filter(item => item.advert?.id != null)
                .map(item => ({ id: item.advert!.id, label: `${item.advert?.title || ""} | ${item.advert!.id}` }));
        }
        case 5: {
            const response = await request.service.get<PopUpAdvertInformationSelectorItem[]>(
                "/dataService/admin/information/selector",
                { params: { title: keyword } }
            );
            return (response.data || []).map(item => ({ id: item.id, label: item.title || String(item.id) }));
        }
        default:
            return [];
    }
}

function normalizeContent(content?: PopUpAdvertDetail["content"]): PopUpAdvertContentForm[] {
    return content?.length
        ? content.map(item => ({
              picFileId: item.picFileId,
              picFileUrl: item.picFileUrl,
              type: item.type == null ? undefined : Number(item.type),
              value: item.value || "",
              sort: item.sort || 1
          }))
        : [{ ...defaultContent }];
}

/** 将表单内部字符串输入转为 Vue 新增/编辑接口 payload。 */
function buildPayload(values: PopUpAdvertFormValues): PopUpAdvertFormValues {
    return {
        name: values.name.trim(),
        terminal: values.terminal,
        displayPage: values.displayPage,
        displayFrequency: values.displayFrequency,
        content: values.content.map(item => ({
            picFileId: item.picFileId,
            type: item.type,
            value: item.value?.trim(),
            sort: item.sort || 1
        }))
    };
}

/** 弹窗广告新增/编辑表单，保留 Vue 终端分支和内容数组约束。 */
export function PopUpAdvertFormPage({
    id: propId,
    // 路由只传入页面组件，编辑态必须复用 Vue 对应的详情接口完成回显。
    loadDetail = loadPopUpAdvertDetail,
    loadPicture = loadPopUpAdvertFormPicture,
    loadContentOptions = loadPopUpAdvertContentOptions,
    create = createPopUpAdvert,
    update = updatePopUpAdvert
}: PopUpAdvertFormPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = propId ?? Number(searchParams.get("id"));
    const [values, setValues] = useState<PopUpAdvertFormValues>(defaultValues);
    const [terminalType, setTerminalType] = useState(1);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<Record<number, UploadUserFile[]>>({});
    const [contentOptions, setContentOptions] = useState<Record<number, PopUpAdvertSelectorOption[]>>({});
    const [loading, setLoading] = useState(Boolean(id > 0 && loadDetail));

    useEffect(() => {
        if (!(id > 0 && loadDetail)) return;
        let disposed = false;
        void loadDetail(id)
            .then(detail => {
                if (disposed) return;
                const terminal = (detail.terminal || [1]).map(Number);
                setValues({
                    name: detail.name || "",
                    terminal,
                    displayPage: detail.displayPage == null ? undefined : Number(detail.displayPage),
                    displayFrequency: detail.displayFrequency == null ? undefined : Number(detail.displayFrequency),
                    content: normalizeContent(detail.content)
                });
                // Vue 编辑态会按图片文件 ID 拉取路径，补齐图片卡片预览。
                void Promise.all(
                    normalizeContent(detail.content).map(async item => {
                        if (item.picFileId == null || item.picFileUrl) return item.picFileUrl;
                        try {
                            return (await loadPicture(item.picFileId)).path || "";
                        } catch {
                            return "";
                        }
                    })
                ).then(paths => {
                    if (disposed) return;
                    setValues(current => ({
                        ...current,
                        content: current.content.map((item, index) => ({
                            ...item,
                            picFileUrl: paths[index] || item.picFileUrl
                        }))
                    }));
                });
                const nextTerminalType = terminal.length === 1 && terminal[0] === 1 ? 1 : 2;
                setTerminalType(nextTerminalType);
                // Vue 编辑态会预加载已保存的远程跳转值，确保选择器回显业务名称而不是裸 ID。
                void Promise.all(
                    (detail.content || []).map(async (item, index) => {
                        if (!item.value || !item.type || Number(item.type) === 6 || Number(item.type) === 7) {
                            return { index, options: [] };
                        }
                        try {
                            return {
                                index,
                                options: await loadContentOptions(item.type, item.value, nextTerminalType)
                            };
                        } catch {
                            return { index, options: [] };
                        }
                    })
                ).then(results => {
                    if (disposed) return;
                    setContentOptions(current => {
                        const next = { ...current };
                        results.forEach(result => {
                            next[result.index] = result.options;
                        });
                        return next;
                    });
                });
                setLoading(false);
            })
            .catch(() => {
                if (disposed) return;
                setErrorMessage("弹窗广告详情加载失败");
                setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [id, loadContentOptions, loadDetail, loadPicture]);

    const updateValue = <K extends keyof PopUpAdvertFormValues>(key: K, value: PopUpAdvertFormValues[K]) => {
        setValues(current => ({ ...current, [key]: value }));
        setErrors(current => ({ ...current, [String(key)]: "" }));
    };

    const changeTerminalType = (nextType: number) => {
        const terminal = nextType === 1 ? [1] : [2, 4];
        setTerminalType(nextType);
        setValues(current => ({
            ...current,
            terminal,
            displayPage: undefined,
            // Vue 切换终端会清空旧内容的跳转选择，避免提交不适用的类型和值。
            content: current.content.map(item => ({ ...item, type: undefined, value: "" }))
        }));
        setContentOptions({});
        setErrors({});
    };

    const updateContent = (index: number, patch: Partial<PopUpAdvertContentForm>) => {
        setValues(current => ({
            ...current,
            content: current.content.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
        }));
        setErrors(current => {
            const next = { ...current };
            Object.keys(next)
                .filter(key => key.startsWith(`content-${index}-`))
                .forEach(key => delete next[key]);
            return next;
        });
    };

    /** 按旧 Vue remote-select 行为查询当前内容模块的跳转候选项。 */
    const searchContentOptions = async (index: number, type: number | string, keyword: string) => {
        if (!keyword.trim()) {
            setContentOptions(current => ({ ...current, [index]: [] }));
            return;
        }
        try {
            const options = await loadContentOptions(type, keyword, terminalType);
            setContentOptions(current => ({ ...current, [index]: options || [] }));
        } catch {
            setContentOptions(current => ({ ...current, [index]: [] }));
            setErrorMessage("跳转内容搜索失败");
        }
    };

    /** 使用 zhihao Upload 自定义请求上传内容图片，并将文件 ID 回填到对应内容模块。 */
    const uploadContentImage = async (index: number, { file, onError, onSuccess }: UploadRequestOptions) => {
        try {
            if (!isValidPopUpImage(file)) throw new Error("invalid image");
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fileName", file.name);
            // 延迟加载上传服务，避免表单首屏依赖上传模块。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const fileId = Number(uploadedFile.id);
            if (!Number.isInteger(fileId) || fileId <= 0) throw new Error("invalid file id");
            updateContent(index, { picFileId: fileId, picFileUrl: uploadedFile.path || "" });
            setUploadedFiles(current => ({
                ...current,
                [index]: [{ uid: fileId, name: file.name, status: "success", response: uploadedFile }]
            }));
            setErrorMessage(null);
            onSuccess(uploadedFile);
        } catch {
            setErrorMessage("图片上传失败");
            onError(new Error("图片上传失败"));
        }
    };

    const validate = () => {
        const nextErrors: Record<string, string> = {};
        if (!values.name.trim()) nextErrors.name = "请输入弹窗名称";
        if (!values.terminal.length) nextErrors.terminal = "请选择适用终端";
        if (values.displayPage == null) nextErrors.displayPage = "请选择显示页面";
        if (values.displayFrequency == null) nextErrors.displayFrequency = "请选择显示频率";
        values.content.forEach((item, index) => {
            if (!item.picFileId) nextErrors[`content-${index}-image`] = "请上传广告图片";
            if (!item.type) nextErrors[`content-${index}-type`] = "请选择跳转类型";
            if (item.type && !item.value?.trim()) nextErrors[`content-${index}-value`] = "请输入跳转值";
            if (
                terminalType === 2 &&
                (item.sort == null || !Number.isInteger(item.sort) || item.sort < 1 || item.sort > 4)
            )
                nextErrors[`content-${index}-sort`] = "请输入1-4之间数字";
        });
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        try {
            const payload = buildPayload(values);
            if (id > 0) await update(id, payload);
            else await create(payload);
            notifyOperationSuccess("已提交");
            await navigate("/userOperation/adBanner/popUpAdvert");
        } catch {
            setErrorMessage("弹窗广告保存失败");
        }
    };

    const addContent = () => {
        if (values.content.length >= 4) return;
        setValues(current => ({
            ...current,
            content: [...current.content, { ...defaultContent, sort: current.content.length + 1 }]
        }));
    };

    const deleteContent = (index: number) => {
        if (values.content.length === 1) {
            setErrorMessage("至少保留一个弹窗内容模块");
            return;
        }
        setValues(current => ({
            ...current,
            content: current.content.filter((_item, itemIndex) => itemIndex !== index)
        }));
        setContentOptions(current => {
            const next: Record<number, PopUpAdvertSelectorOption[]> = {};
            Object.entries(current).forEach(([key, options]) => {
                const oldIndex = Number(key);
                if (oldIndex < index) next[oldIndex] = options;
                if (oldIndex > index) next[oldIndex - 1] = options;
            });
            return next;
        });
    };

    const visibleContentTypes =
        terminalType === 1
            ? contentTypes.filter(option => option.value === 4 || option.value === 7)
            : contentTypes.filter(option => option.value !== 7);

    if (loading) return <section aria-label="pop-up-advert-form-page">加载中</section>;

    return (
        <section
            aria-label="pop-up-advert-form-page"
            className="pop-up-advert-form-page flex h-full min-h-full flex-col"
        >
            <Card className="flex h-full flex-1 flex-col">
                <Typography.Title className="mb-4 mt-0" level={4}>
                    {id > 0 ? "修改弹框广告页" : "新增弹框广告页"}
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <ZhGrid className="pop-up-advert-form-grid" cols={2} gap={[16, 0]}>
                    <Form labelAlign="left" labelCol={{ flex: "90px" }}>
                        <Form.Item
                            help={errors.name}
                            label="弹窗名称"
                            required
                            validateStatus={errors.name ? "error" : undefined}
                        >
                            <Input
                                allowClear
                                aria-label="弹窗名称"
                                maxLength={50}
                                placeholder="请输入弹窗名称"
                                showCount
                                value={values.name}
                                onChange={event => updateValue("name", event.target.value)}
                            />
                        </Form.Item>
                        <Form.Item
                            help={errors.terminal}
                            label="适用终端"
                            required
                            validateStatus={errors.terminal ? "error" : undefined}
                        >
                            <Radio.Group
                                aria-label="适用终端类型"
                                options={[
                                    { label: "PC端", value: 1 },
                                    { label: "移动端", value: 2 }
                                ]}
                                value={terminalType}
                                onChange={event => changeTerminalType(Number(event.target.value))}
                            />
                        </Form.Item>
                        {terminalType === 2 ? (
                            <Form.Item label="" required>
                                <Checkbox.Group
                                    aria-label="适用终端"
                                    options={[
                                        { value: 4, label: "链云APP" },
                                        { value: 2, label: "链云小程序" }
                                    ]}
                                    value={values.terminal}
                                    onChange={terminal => updateValue("terminal", terminal.map(Number))}
                                />
                            </Form.Item>
                        ) : null}
                        <ZhGrid cols={2} gap={[16, 0]}>
                            <Form.Item
                                help={errors.displayPage}
                                label="显示页面"
                                required
                                validateStatus={errors.displayPage ? "error" : undefined}
                            >
                                <Select
                                    allowClear
                                    aria-label="显示页面"
                                    options={terminalType === 1 ? displayPages.slice(0, 1) : displayPages}
                                    placeholder="请选择显示页面"
                                    value={values.displayPage}
                                    onChange={displayPage => updateValue("displayPage", displayPage)}
                                />
                            </Form.Item>
                            <Form.Item
                                help={errors.displayFrequency}
                                label="显示频率"
                                required
                                validateStatus={errors.displayFrequency ? "error" : undefined}
                            >
                                <Select
                                    allowClear
                                    aria-label="显示频率"
                                    options={frequencies}
                                    placeholder="请选择显示频率"
                                    value={values.displayFrequency}
                                    onChange={displayFrequency => updateValue("displayFrequency", displayFrequency)}
                                />
                            </Form.Item>
                        </ZhGrid>
                    </Form>
                </ZhGrid>
                <div className="popup-advert-content-grid grid grid-cols-2 gap-4">
                    {values.content.map((item, index) => (
                        <Card className="relative bg-[#fafafa]" key={index} size="small">
                            <Space orientation="vertical">
                                {terminalType === 2 ? (
                                    <Space className="absolute right-3 top-3">
                                        {index === values.content.length - 1 && values.content.length < 4 ? (
                                            <Button
                                                aria-label="新增内容模块"
                                                shape="circle"
                                                type="primary"
                                                onClick={addContent}
                                            >
                                                +
                                            </Button>
                                        ) : null}
                                        <Button
                                            aria-label={`删除内容模块 ${index + 1}`}
                                            shape="circle"
                                            type="primary"
                                            onClick={() => deleteContent(index)}
                                        >
                                            −
                                        </Button>
                                    </Space>
                                ) : null}
                                <Form.Item
                                    help={errors[`content-${index}-image`]}
                                    label={
                                        <span className="popup-advert-image-label">
                                            <span>上传图片</span>
                                            <span>
                                                图片上传受限制，仅支持JPEG、PNG格式，
                                                {terminalType === 2 ? "单张最大5MB，3：4宽高比例的图片，" : ""}
                                                请勿包含敏感信息或侵权内容
                                            </span>
                                        </span>
                                    }
                                    labelCol={{ span: 24 }}
                                    required
                                    validateStatus={errors[`content-${index}-image`] ? "error" : undefined}
                                >
                                    <div className="popup-advert-image-upload">
                                        <ZhUpload
                                            action=""
                                            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                                            aria-label={`内容模块 ${index + 1}图片上传`}
                                            fileList={uploadedFiles[index] || []}
                                            limit={1}
                                            beforeUpload={file => {
                                                if (isValidPopUpImage(file)) return true;
                                                setErrorMessage(
                                                    file.type.startsWith("image/")
                                                        ? "大小不能超过5MB"
                                                        : "仅支持 JPEG、PNG 格式"
                                                );
                                                return false;
                                            }}
                                            httpRequest={options => uploadContentImage(index, options)}
                                            showFileList={false}
                                            onRemove={() => {
                                                updateContent(index, { picFileId: undefined, picFileUrl: undefined });
                                                setUploadedFiles(current => ({ ...current, [index]: [] }));
                                            }}
                                        >
                                            <div className="popup-advert-image-dropzone">
                                                {item.picFileUrl ? (
                                                    <img
                                                        alt={`弹窗广告图片预览 ${index + 1}`}
                                                        className="admin-upload-preview-media"
                                                        src={item.picFileUrl}
                                                    />
                                                ) : null}
                                                {item.picFileUrl ? (
                                                    <span className="popup-advert-image-controls">
                                                        <button
                                                            type="button"
                                                            onClick={event => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                updateContent(index, {
                                                                    picFileId: undefined,
                                                                    picFileUrl: undefined
                                                                });
                                                                setUploadedFiles(current => ({
                                                                    ...current,
                                                                    [index]: []
                                                                }));
                                                            }}
                                                        >
                                                            <i aria-hidden="true" className="iconfont icon-delete" />
                                                            删除
                                                        </button>
                                                        <span>
                                                            <i
                                                                aria-hidden="true"
                                                                className="iconfont icon-PictureFilled"
                                                            />
                                                            替换图片
                                                        </span>
                                                    </span>
                                                ) : (
                                                    <span className="popup-advert-image-upload-action">
                                                        <i aria-hidden="true" className="iconfont icon-shangchuan" />
                                                        <span>上传轮播图</span>
                                                    </span>
                                                )}
                                            </div>
                                        </ZhUpload>
                                    </div>
                                </Form.Item>
                                <Form.Item
                                    help={errors[`content-${index}-type`]}
                                    label="跳转类型"
                                    required
                                    validateStatus={errors[`content-${index}-type`] ? "error" : undefined}
                                >
                                    <Radio.Group
                                        aria-label={`跳转类型 ${index + 1}`}
                                        options={visibleContentTypes}
                                        value={item.type ?? ""}
                                        onChange={event =>
                                            // Vue 切换跳转类型会清空旧值，避免不同类型的值交叉提交。
                                            updateContent(index, {
                                                type: Number(event.target.value) || undefined,
                                                value: ""
                                            })
                                        }
                                    />
                                </Form.Item>
                                {item.type || terminalType === 2 ? (
                                    <ZhGrid cols={2} gap={[20, 0]}>
                                        {item.type ? (
                                            <Form.Item
                                                help={errors[`content-${index}-value`]}
                                                label={remoteContentTypeLabels[Number(item.type)]?.label || "跳转值"}
                                                validateStatus={errors[`content-${index}-value`] ? "error" : undefined}
                                            >
                                                {Number(item.type) === 6 ? (
                                                    <Select
                                                        allowClear
                                                        aria-label={`跳转值 ${index + 1}`}
                                                        options={POP_UP_AD_FUNCTION_PAGE.options.map(option => ({
                                                            label: option.label,
                                                            value: String(option.value)
                                                        }))}
                                                        placeholder="请搜索功能页"
                                                        value={item.value || ""}
                                                        // 选项数量固定且很少，关闭虚拟滚动以保持旧 Vue 下拉项的完整可访问性。
                                                        virtual={false}
                                                        onChange={value => updateContent(index, { value })}
                                                    />
                                                ) : remoteContentTypeLabels[Number(item.type)] ? (
                                                    <Select
                                                        allowClear
                                                        aria-label={`跳转值 ${index + 1}`}
                                                        filterOption={false}
                                                        options={(contentOptions[index] || []).map(option => ({
                                                            label: option.label,
                                                            value: String(option.id)
                                                        }))}
                                                        placeholder={
                                                            remoteContentTypeLabels[Number(item.type)].placeholder
                                                        }
                                                        showSearch
                                                        value={item.value || undefined}
                                                        onChange={value => updateContent(index, { value })}
                                                        onSearch={keyword =>
                                                            void searchContentOptions(index, item.type || 0, keyword)
                                                        }
                                                    />
                                                ) : (
                                                    <Input
                                                        aria-label={`跳转值 ${index + 1}`}
                                                        maxLength={item.type === 7 ? 64 : undefined}
                                                        placeholder={item.type === 7 ? "请输入跳转链接" : undefined}
                                                        value={item.value || ""}
                                                        onChange={event =>
                                                            updateContent(index, { value: event.target.value })
                                                        }
                                                    />
                                                )}
                                            </Form.Item>
                                        ) : (
                                            <div aria-hidden="true" />
                                        )}
                                        {terminalType === 2 ? (
                                            <Form.Item
                                                help={errors[`content-${index}-sort`]}
                                                label="排序"
                                                required
                                                extra="请输入1-4之间数字，数字越大权重越高，优先显示权重高的广告"
                                                validateStatus={errors[`content-${index}-sort`] ? "error" : undefined}
                                            >
                                                <ZhInputNumber
                                                    aria-label={`排序 ${index + 1}`}
                                                    min={1}
                                                    max={4}
                                                    precision={0}
                                                    value={item.sort ?? 1}
                                                    onChange={value =>
                                                        updateContent(index, { sort: Number(value) || undefined })
                                                    }
                                                />
                                            </Form.Item>
                                        ) : null}
                                    </ZhGrid>
                                ) : null}
                            </Space>
                        </Card>
                    ))}
                </div>
                {!values.content.length ? <Typography.Text>{DEFAULT_VALUE}</Typography.Text> : null}
            </Card>
            <Space className="operation-fixed-btn" size={0}>
                <Button aria-label="提交弹窗广告" type="primary" onClick={() => void submit()}>
                    提交
                </Button>
                <Button onClick={() => void navigate(-1)}>返回</Button>
            </Space>
        </section>
    );
}
